import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Lightbulb, Pill, Activity, MessageCircle, ShieldCheck, ArrowRight, Sparkles } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import VideoWithSoundToggle from '../components/VideoWithSoundToggle';
import { COLORS } from '../data/mockData';

const SECTIONS = [
  {
    icon: Trophy,
    title: 'Competitions & Challenges',
    desc: 'Step counts, sleep streaks, mindfulness minutes — compete on weekly leaderboards with badges and credit rewards.',
    color: '#DAA520',
  },
  {
    icon: Lightbulb,
    title: 'Hacks & Tips',
    desc: 'Share what\'s working for you — morning routines, biohacks, recovery rituals. Top-voted tips of the month earn AI-coaching credits.',
    color: '#4682B4',
  },
  {
    icon: Pill,
    title: 'Supplements & Stacks',
    desc: 'Real members log what they take and how it\'s affecting their health scores. Crowd-sourced — never medical advice.',
    color: '#8B008B',
  },
  {
    icon: Activity,
    title: 'Activities & Routines',
    desc: 'From cold plunges to Zone-2 cardio, see what your community is doing and how it maps to their SHIELDED pillars.',
    color: '#4CAF50',
  },
  {
    icon: MessageCircle,
    title: 'Q&A Board',
    desc: 'Pose a longevity-themed question; community members answer. Up-vote helpful answers — top responders earn credits each month.',
    color: '#FF9800',
  },
];

export default function YourCommunityPage() {
  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: '100vh' }}>
      <SiteHeader />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(180deg, #F9F6F0 0%, #E8ECD7 60%, #F9F6F0 100%)', padding: '56px 20px 40px', textAlign: 'center' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2, textTransform: 'uppercase' }}>YOUR COMMUNITY</span>
          <h1 data-testid="community-title" style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 900, color: COLORS.textPrimary, lineHeight: 1.15, letterSpacing: -1, marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>
            Longevity is a team sport.
          </h1>
          <p style={{ fontSize: 'clamp(16px, 2vw, 18px)', color: COLORS.textSecondary, lineHeight: 1.6, marginTop: 14 }}>
            Compete, share, ask, and learn alongside thousands of members optimizing their healthspan. Up-voted hacks and answers earn AI-coaching credits.
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, backgroundColor: '#FFF', padding: '6px 14px', borderRadius: 9999, marginTop: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Sparkles size={14} color={COLORS.tierOptimizer} />
            <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>Launching March 2026 — request early access below</span>
          </div>
        </div>
      </section>

      {/* INTRO VIDEO (under banner, top-middle, 65% size) */}
      <section data-testid="community-intro-video-section" style={{ padding: '20px 20px 0' }}>
        <VideoWithSoundToggle
          src="/media/community-intro.mp4"
          testId="community-intro-video"
          aspectRatio="16 / 9"
          maxWidth={252}
          objectFit="cover"
        />
      </section>

      {/* Sections grid */}
      <section style={{ padding: '40px 20px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 18 }}>
          {SECTIONS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} data-testid={`community-section-${i}`} style={{ backgroundColor: COLORS.surface, borderRadius: 20, padding: 22, boxShadow: '0 2px 10px rgba(0,0,0,0.04)', transition: 'transform 0.3s, box-shadow 0.3s' }} className="hover-lift">
                <div style={{ width: 48, height: 48, borderRadius: 14, backgroundColor: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Icon size={24} color={s.color} />
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 6 }}>{s.title}</h3>
                <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.5, margin: 0 }}>{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Disclaimers + guardrails */}
      <section style={{ padding: '24px 20px 48px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', backgroundColor: '#FFF8E1', border: '1px solid #F0D77A', borderRadius: 16, padding: '20px 22px', display: 'flex', gap: 14 }}>
          <ShieldCheck size={22} color="#B58A00" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#7A5C00', marginBottom: 6 }}>Important: community content is not medical advice</div>
            <p style={{ fontSize: 14, color: '#7A5C00', lineHeight: 1.55, margin: 0 }}>
              Tips, hacks, supplements, and Q&A answers reflect individual member experiences only. They are <strong>not</strong> a substitute for consultation with a licensed healthcare provider. Every post is moderated for safety, and questions outside the scope of longevity, SHIELDED pillars, or general wellness are redirected to professional resources (MedlinePlus, Mayo Clinic, CDC).
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg, #6B8E23, #4a6e18)', padding: '48px 20px', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: '#FFF', fontFamily: 'Outfit, sans-serif' }}>Be among the first members.</h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)', marginTop: 8, marginBottom: 22 }}>Get early access to challenges, the Q&A board, and the credit-reward program.</p>
          <Link to="/register" data-testid="community-cta-register" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, backgroundColor: '#FFF', borderRadius: 9999, padding: '14px 28px', textDecoration: 'none' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.primary }}>Reserve My Spot</span>
            <ArrowRight size={18} color={COLORS.primary} />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
